import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FiUploadCloud, FiDownload, FiFile, FiArrowLeft, FiCheckCircle, FiAlertTriangle, FiClock, FiRefreshCw,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { Card, Button, Toggle, Spinner } from '../../components/common';
import ImportPreviewTable, { PREVIEW_FILTERS } from './components/ImportPreviewTable';
import { billingImportService } from '../../services/billingImportService';
import { billingFiles } from '../../services/billingFiles';
import { getApiError, getApiFieldErrors } from '../../services/cmsService';
import { formatDate } from '../../utils/dateUtils';
import { formatINR, getAllApiErrors } from './billingUtils';
import './Billing.css';
import './ClientImportPage.css';

const MAX_BYTES = 5 * 1024 * 1024;
const ALLOWED = ['csv', 'xlsx'];

const DEFAULT_OPTIONS = {
  overwriteExisting: false,
  skipRowsWithErrors: true,
  importInvalidGstin: false,
  createDraftInvoices: false,
};

const SUMMARY_CHIPS = [
  { key: 'clients', label: 'Clients' },
  { key: 'clients_new', label: 'New' },
  { key: 'clients_matched', label: 'Matched' },
  { key: 'possible_matches', label: 'Possible matches', tone: 'warning' },
  { key: 'projects', label: 'Projects' },
  { key: 'duplicates', label: 'Duplicates' },
  { key: 'errors', label: 'Errors', tone: 'danger' },
  { key: 'warnings', label: 'Warnings', tone: 'warning' },
  { key: 'invalid_gstins', label: 'Invalid GSTINs', tone: 'warning' },
];

const extensionOf = (name = '') => name.split('.').pop().toLowerCase();

const minutesLeft = (expiresAt) => {
  if (!expiresAt) return null;
  return Math.floor((new Date(expiresAt).getTime() - Date.now()) / 60000);
};

const ClientImportPage = () => {
  const fileInputRef = useRef(null);
  const [step, setStep] = useState('upload'); // upload | preview | result
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [fileError, setFileError] = useState('');

  const [preview, setPreview] = useState(null);
  const [filter, setFilter] = useState('all');
  const [decisions, setDecisions] = useState({});
  const [options, setOptions] = useState(DEFAULT_OPTIONS);
  const [committing, setCommitting] = useState(false);
  const [commitErrors, setCommitErrors] = useState([]);
  const [remaining, setRemaining] = useState(null);

  const [result, setResult] = useState(null);
  const [history, setHistory] = useState(null);
  const [historyKey, setHistoryKey] = useState(0);

  /* ---------------- Recent imports ---------------- */
  useEffect(() => {
    if (step !== 'upload') return;
    billingImportService
      .history({ per_page: 5 })
      .then((page) => setHistory(page.data))
      .catch(() => setHistory([]));
  }, [step, historyKey]);

  /* ---------------- Token countdown ---------------- */
  useEffect(() => {
    if (step !== 'preview' || !preview?.expires_at) return undefined;
    const tick = () => setRemaining(minutesLeft(preview.expires_at));
    tick();
    const timer = setInterval(tick, 15000);
    return () => clearInterval(timer);
  }, [step, preview]);

  const expired = remaining !== null && remaining < 0;

  /* ---------------- Upload ---------------- */
  const handleFile = async (file) => {
    if (!file) return;
    setFileError('');
    if (!ALLOWED.includes(extensionOf(file.name))) {
      setFileError('Choose a .csv or .xlsx file.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setFileError('The file is larger than 5 MB.');
      return;
    }

    setUploading(true);
    setProgress(0);
    try {
      const data = await billingImportService.preview(file, (e) => {
        if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
      });
      setPreview(data);
      setDecisions({});
      setOptions(DEFAULT_OPTIONS);
      setFilter('all');
      setCommitErrors([]);
      setStep('preview');
    } catch (error) {
      const fields = getApiFieldErrors(error);
      setFileError(fields.file || getApiError(error, 'Could not read the file'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (!uploading) handleFile(e.dataTransfer.files?.[0]);
  };

  const downloadTemplate = async () => {
    try {
      await billingFiles.downloadTemplate();
    } catch (error) {
      toast.error(getApiError(error, 'Could not download the template'));
    }
  };

  /* ---------------- Preview ---------------- */
  const blocks = useMemo(() => preview?.clients ?? [], [preview]);
  const possibleMatches = blocks.filter((b) => b.action === 'possible_match');
  const undecided = possibleMatches.filter((b) => !decisions[b.key]);
  const summary = preview?.summary ?? {};

  const filterCounts = useMemo(
    () => Object.fromEntries(PREVIEW_FILTERS.map((f) => [f.value, blocks.filter(f.test).length])),
    [blocks]
  );
  const visibleBlocks = useMemo(
    () => blocks.filter(PREVIEW_FILTERS.find((f) => f.value === filter)?.test ?? (() => true)),
    [blocks, filter]
  );

  const setOption = (key) => (e) => setOptions((prev) => ({ ...prev, [key]: e.target.checked }));

  const startOver = () => {
    setPreview(null);
    setResult(null);
    setDecisions({});
    setCommitErrors([]);
    setStep('upload');
    setHistoryKey((k) => k + 1);
  };

  const handleCommit = async () => {
    setCommitting(true);
    setCommitErrors([]);
    try {
      const data = await billingImportService.commit({
        previewToken: preview.preview_token,
        decisions: possibleMatches.map((b) => {
          const d = decisions[b.key];
          return d.action === 'merge'
            ? { row_numbers: b.row_numbers, action: 'merge', client_id: d.clientId }
            : { row_numbers: b.row_numbers, action: 'create' };
        }),
        ...options,
      });
      setResult(data);
      setStep('result');
      toast.success('Import completed');
    } catch (error) {
      const fields = getApiFieldErrors(error);
      if (fields.preview_token) {
        toast.error(fields.preview_token);
        startOver();
        return;
      }
      // `decisions` (and others) can carry several messages — show all of them.
      const messages = getAllApiErrors(error);
      setCommitErrors(messages.length ? messages : [getApiError(error, 'Import failed')]);
      toast.error(getApiError(error, 'Import failed'));
    } finally {
      setCommitting(false);
    }
  };

  /* ---------------- Render ---------------- */
  return (
    <div className="bl-page client-import">
      <Link to="/billing/clients" className="bl-back">
        <FiArrowLeft /> Back to billing clients
      </Link>

      <ol className="ci-steps" aria-label="Import steps">
        {['Upload', 'Review', 'Done'].map((label, index) => {
          const current = ['upload', 'preview', 'result'].indexOf(step);
          return (
            <li key={label} className={`ci-step ${index === current ? 'is-current' : ''} ${index < current ? 'is-done' : ''}`}>
              <span className="ci-step-num">{index < current ? <FiCheckCircle /> : index + 1}</span>
              {label}
            </li>
          );
        })}
      </ol>

      {step === 'upload' && (
        <>
          <Card
            title="Import billing clients"
            subtitle="CSV or Excel (.xlsx), up to 5 MB and 5,000 rows. Nothing is saved until you confirm the preview."
            action={
              <Button variant="outline" icon={<FiDownload />} onClick={downloadTemplate}>
                Download template
              </Button>
            }
          >
            <div
              className={`ci-drop ${dragActive ? 'is-active' : ''} ${uploading ? 'is-busy' : ''}`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragActive(true);
              }}
              onDragLeave={() => setDragActive(false)}
              onDrop={onDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx"
                hidden
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
              {uploading ? (
                <div className="ci-progress">
                  <Spinner size="medium" />
                  <span>{progress < 100 ? `Uploading… ${progress}%` : 'Reading the file…'}</span>
                  <div className="ci-bar"><span style={{ width: `${progress}%` }} /></div>
                </div>
              ) : (
                <>
                  <FiUploadCloud className="ci-drop-icon" />
                  <p className="ci-drop-title">Drag and drop your file here</p>
                  <p className="bl-muted">One row per project. A row without a client name continues the client above it.</p>
                  <Button icon={<FiFile />} onClick={() => fileInputRef.current?.click()}>
                    Choose file
                  </Button>
                </>
              )}
            </div>
            {fileError && (
              <div className="bl-banner danger ci-file-error" role="alert">
                <FiAlertTriangle />
                <div className="bl-banner-body">{fileError}</div>
              </div>
            )}
          </Card>

          <Card title="Recent imports" padding="medium">
            {history === null ? (
              <div className="bl-center" style={{ minHeight: 120 }}><Spinner /></div>
            ) : history.length === 0 ? (
              <div className="bl-empty">No imports yet.</div>
            ) : (
              <div className="bl-table-wrap">
                <table className="bl-table">
                  <thead>
                    <tr>
                      <th>File</th>
                      <th>When</th>
                      <th className="num">Clients new / updated</th>
                      <th className="num">Projects</th>
                      <th className="num">Skipped</th>
                      <th aria-label="Report" />
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((batch) => (
                      <tr key={batch.id}>
                        <td>{batch.original_filename}</td>
                        <td>{formatDate(batch.created_at, 'datetime')}</td>
                        <td className="num">{batch.clients_created} / {batch.clients_updated}</td>
                        <td className="num">{batch.projects_created}</td>
                        <td className="num">{batch.rows_skipped}</td>
                        <td>
                          <button
                            type="button"
                            className="action-btn"
                            title="Download report"
                            onClick={() => billingFiles.downloadImportReport(batch.id).catch((err) => toast.error(getApiError(err, 'No report for this import')))}
                          >
                            <FiDownload />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      {step === 'preview' && preview && (
        <>
          <Card
            title={`Review: ${preview.original_filename}`}
            subtitle={`Header found on row ${preview.header_row}. Check each client, then confirm.`}
            action={
              <Button variant="ghost" icon={<FiRefreshCw />} onClick={startOver}>
                Upload a different file
              </Button>
            }
          >
            <div className="ci-summary">
              {SUMMARY_CHIPS.filter((c) => c.key !== 'invalid_gstins' || summary.invalid_gstins > 0).map((chip) => (
                <div key={chip.key} className={`ci-summary-chip ${chip.tone && summary[chip.key] > 0 ? `tone-${chip.tone}` : ''}`}>
                  <span className="ci-summary-value">{summary[chip.key] ?? 0}</span>
                  <span className="ci-summary-label">{chip.label}</span>
                </div>
              ))}
            </div>

            {preview.header_warnings?.length > 0 && (
              <div className="bl-banner warning">
                <FiAlertTriangle />
                <div className="bl-banner-body">
                  <ul>{preview.header_warnings.map((w) => <li key={w}>{w}</li>)}</ul>
                </div>
              </div>
            )}

            <div className={`ci-expiry ${expired ? 'is-expired' : remaining !== null && remaining < 5 ? 'is-soon' : ''}`}>
              <FiClock />
              {expired
                ? 'This preview has expired. Upload the file again to import it.'
                : remaining === null
                  ? 'This preview is valid for 30 minutes.'
                  : `This preview expires in ${Math.max(remaining, 0) < 1 ? 'under a minute' : `${remaining} min`}.`}
            </div>
          </Card>

          <Card>
            <div className="bl-chip-group ci-filters" role="group" aria-label="Filter preview">
              {PREVIEW_FILTERS.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  className={`bl-chip ${filter === f.value ? 'is-active' : ''}`}
                  aria-pressed={filter === f.value}
                  onClick={() => setFilter(f.value)}
                >
                  {f.label} <span className="bl-chip-count">{filterCounts[f.value]}</span>
                </button>
              ))}
            </div>

            <ImportPreviewTable
              blocks={visibleBlocks}
              decisions={decisions}
              onDecide={(key, decision) => setDecisions((prev) => ({ ...prev, [key]: decision }))}
              importInvalidGstin={options.importInvalidGstin}
            />
          </Card>

          <Card title="Import options" className="ci-options-card">
            <div className="ci-options">
              <Toggle
                name="overwrite_existing"
                checked={options.overwriteExisting}
                onChange={setOption('overwriteExisting')}
                label="Overwrite details of matched clients with the file’s values"
              />
              <Toggle
                name="skip_rows_with_errors"
                checked={options.skipRowsWithErrors}
                onChange={setOption('skipRowsWithErrors')}
                label="Skip rows with errors (off: any error cancels the whole import)"
              />
              {summary.invalid_gstins > 0 && (
                <Toggle
                  name="import_invalid_gstin"
                  checked={options.importInvalidGstin}
                  onChange={setOption('importInvalidGstin')}
                  label={`Import the ${summary.invalid_gstins} client${summary.invalid_gstins === 1 ? '' : 's'} with an invalid GSTIN (saved without a GSTIN)`}
                />
              )}
              <Toggle
                name="create_draft_invoices"
                checked={options.createDraftInvoices}
                onChange={setOption('createDraftInvoices')}
                label="Also create a draft invoice per client from the imported projects"
              />
            </div>

            {commitErrors.length > 0 && (
              <div className="bl-banner danger" role="alert">
                <FiAlertTriangle />
                <div className="bl-banner-body">
                  Nothing was imported:
                  <ul>{commitErrors.map((msg) => <li key={msg}>{msg}</li>)}</ul>
                </div>
              </div>
            )}

            <div className="ci-confirm">
              {undecided.length > 0 && (
                <span className="ci-confirm-hint">
                  Choose Merge or Create new for {undecided.length} possible match{undecided.length === 1 ? '' : 'es'}.
                </span>
              )}
              <Button variant="ghost" onClick={startOver} disabled={committing}>Cancel</Button>
              <Button onClick={handleCommit} loading={committing} disabled={undecided.length > 0 || expired}>
                Confirm import
              </Button>
            </div>
          </Card>
        </>
      )}

      {step === 'result' && result && (
        <>
          <Card title="Import complete" icon={<FiCheckCircle />}>
            <div className="bl-stats">
              <div className="bl-stat tone-success">
                <span className="bl-stat-label">Clients created</span>
                <span className="bl-stat-value">{result.counts.clients_created}</span>
              </div>
              <div className="bl-stat">
                <span className="bl-stat-label">Clients updated</span>
                <span className="bl-stat-value">{result.counts.clients_updated}</span>
              </div>
              <div className="bl-stat tone-success">
                <span className="bl-stat-label">Projects created</span>
                <span className="bl-stat-value">{result.counts.projects_created}</span>
              </div>
              <div className="bl-stat">
                <span className="bl-stat-label">Duplicates skipped</span>
                <span className="bl-stat-value">{result.counts.duplicates_skipped}</span>
              </div>
              <div className={`bl-stat ${result.counts.rows_skipped > 0 ? 'tone-warning' : ''}`}>
                <span className="bl-stat-label">Rows skipped</span>
                <span className="bl-stat-value">{result.counts.rows_skipped}</span>
              </div>
            </div>
            <div className="ci-result-actions">
              <Button
                variant="outline"
                icon={<FiDownload />}
                onClick={() => billingFiles.downloadImportReport(result.import_batch_id).catch((err) => toast.error(getApiError(err, 'No report for this import')))}
              >
                Download report
              </Button>
              <Button icon={<FiRefreshCw />} onClick={startOver}>Import another file</Button>
            </div>
          </Card>

          {result.clients?.length > 0 && (
            <Card title="Imported clients" padding="medium">
              <ul className="ci-result-list">
                {result.clients.map((c) => (
                  <li key={c.id}>
                    {c.deleted ? (
                      <span>{c.name} <span className="bl-muted">(deleted)</span></span>
                    ) : (
                      <Link className="bl-link" to={`/billing/clients/${c.id}`}>{c.name}</Link>
                    )}
                    <span className="bl-cell-sub">{[c.client_code, c.gstin].filter(Boolean).join(' · ')}</span>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {result.draft_invoices?.length > 0 && (
            <Card title="Draft invoices created" padding="medium">
              <ul className="ci-result-list">
                {result.draft_invoices.map((d) => (
                  <li key={d.id}>
                    <Link className="bl-link" to={`/billing/invoices/${d.id}/edit`}>Draft for {d.client_name}</Link>
                    <span className="bl-cell-sub">{formatINR(d.total_amount)}</span>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default ClientImportPage;
